// src/services/stockUpdateNotificationService.js
// Service to track stocktake and stock injection updates for toast notifications

import { getFirestore, collection, query, where, onSnapshot, doc, getDoc, Timestamp } from 'firebase/firestore';

const db = getFirestore();

const COLLECTIONS = {
    STOCK_TAKES: 'stockTakes',
    STOCK_MOVEMENTS: 'stockMovements',
    CHEESE_STOCKTAKE_REPORTS: 'cheeseStocktakeReports',
    CHEESE_ADJUSTMENTS: 'cheeseAdjustments'
};

/**
 * Stock Update Notification Service
 * Listens for stocktake completions and stock injections to trigger notifications
 */
class StockUpdateNotificationService {
    constructor() {
        this.subscriptions = new Map();
        this.lastProcessedIds = {
            stockTakes: new Set(),
            stockMovements: new Set(),
            cheeseStocktakes: new Set(),
            cheeseAdjustments: new Set()
        };
        /** In-memory dedupe for notify() (e.g. same event delivered twice in a row). */
        this._notifyDedupeKeys = new Set();
        this.callbacks = [];
    }

    /**
     * Subscribe to stock update notifications
     * @param {Function} callback - Called with { type, data, timestamp }
     */
    subscribe(callback) {
        this.callbacks.push(callback);
        return () => {
            this.callbacks = this.callbacks.filter(cb => cb !== callback);
        };
    }

    /**
     * Notify all subscribers of a stock update
     */
    notify(type, data) {
        const id = data?.id != null ? String(data.id) : '';
        if (id) {
            const dedupeKey = `${type}:${id}`;
            if (this._notifyDedupeKeys.has(dedupeKey)) {
                console.log('[StockUpdateNotification] Dedupe: skipping duplicate notify', dedupeKey);
                return;
            }
            this._notifyDedupeKeys.add(dedupeKey);
            setTimeout(() => {
                try {
                    this._notifyDedupeKeys.delete(dedupeKey);
                } catch (_) { /* ignore */ }
            }, 8000);
        }
        this.callbacks.forEach(callback => {
            try {
                callback({ type, data, timestamp: new Date() });
            } catch (error) {
                console.error('[StockUpdateNotification] Error in callback:', error);
            }
        });
    }

    /**
     * Start listening for stocktake completions
     */
    startListeningStockTakes() {
        if (this.subscriptions.has('stockTakes')) {
            return; // Already listening
        }

        // Listen for completed stocktakes
        const q = query(
            collection(db, COLLECTIONS.STOCK_TAKES),
            where('status', '==', 'completed')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added' || change.type === 'modified') {
                    const data = change.doc.data();
                    const stockTakeId = change.doc.id;

                    // Skip if we've already processed this
                    if (this.lastProcessedIds.stockTakes.has(stockTakeId)) {
                        return;
                    }

                    // Only notify if it was just completed (has completedAt timestamp)
                    if (data.completedAt && data.status === 'completed') {
                        // Check if this is a recent completion (within last 5 minutes)
                        const completedAt = data.completedAt.toDate ? data.completedAt.toDate() : new Date(data.completedAt);
                        const now = new Date();
                        const timeDiff = now - completedAt;
                        
                        if (timeDiff < 5 * 60 * 1000) { // Within 5 minutes
                            this.lastProcessedIds.stockTakes.add(stockTakeId);
                            
                            this.notify('stocktake_completed', {
                                id: stockTakeId,
                                startedBy: data.startedBy,
                                completedBy: data.completedBy,
                                completedAt: completedAt,
                                totalSkus: data.totalSkus || 0,
                                totalBatches: data.totalBatches || 0,
                                mode: data.mode || 'replace',
                                variance: data.variance || {},
                                venueId: data.venueId || null,
                                venueName: data.venueName || null
                            });
                        }
                    }
                }
            });
        }, (error) => {
            console.error('[StockUpdateNotification] Error listening to stocktakes:', error);
        });

        this.subscriptions.set('stockTakes', unsubscribe);
    }

    /**
     * Start listening for stock movements (injections/updates)
     */
    startListeningStockMovements() {
        if (this.subscriptions.has('stockMovements')) {
            return; // Already listening
        }

        // Listen for recent stock movements (injections, adjustments)
        const q = query(
            collection(db, COLLECTIONS.STOCK_MOVEMENTS),
            where('timestamp', '>', Timestamp.fromDate(new Date(Date.now() - 5 * 60 * 1000))) // Last 5 minutes
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added') {
                    const data = change.doc.data();
                    const movementId = change.doc.id;

                    // Skip if we've already processed this
                    if (this.lastProcessedIds.stockMovements.has(movementId)) {
                        return;
                    }

                    // Only notify for meaningful stock injections/adjustments
                    const movementType = data.type || data.movementType;
                    const delta = data.deltaUnits || data.quantity || data.delta || 0;
                    const isPositiveChange = delta > 0;
                    
                    // Notify for:
                    // - Production injections (from Quick Slice or Production Toolkit)
                    // - Manual adjustments (positive changes only - injections)
                    // - Admin-initiated changes
                    // - Any movement explicitly marked as injection
                    const isInjection = isPositiveChange && (
                        movementType === 'production' ||
                        movementType === 'adjustment' || 
                        movementType === 'manual_adjustment' ||
                        data.sourceSystem === 'admin' ||
                        data.sourceSystem === 'manual' ||
                        data.sourceSystem === 'production' ||
                        data.reason?.toLowerCase().includes('inject') ||
                        data.reason?.toLowerCase().includes('manual injection') ||
                        data.reason?.toLowerCase().includes('quick slice') ||
                        data.metadata?.source === 'quick_slice_injection'
                    );
                    
                    if (isInjection) {
                        
                        this.lastProcessedIds.stockMovements.add(movementId);
                        
                        // Extract correct field names from stock movement document
                        const deltaUnits = data.deltaUnits || data.quantity || data.delta || 0;
                        const afterTotal = data.afterTotal || data.newStock || data.currentStock || 0;
                        const beforeTotal = data.beforeTotal || data.previousStock || 0;
                        const lotCode = data.lotCode || data.batchNumber || data.batch || null;
                        const productName = data.productName || data.metadata?.productName || data.metadata?.name || data.sku;
                        
                        this.notify('stock_injection', {
                            id: movementId,
                            sku: data.sku,
                            productName: productName,
                            quantity: deltaUnits,
                            previousStock: beforeTotal,
                            newStock: afterTotal,
                            lotCode: lotCode,
                            userId: data.userId || data.user?.uid || 'unknown',
                            userName: data.userName || data.user?.displayName || 'Unknown',
                            reason: data.reason || 'Stock injection',
                            timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : new Date(),
                            sourceSystem: data.sourceSystem || 'manual',
                            metadata: data.metadata || {}
                        });
                    }
                }
            });
        }, (error) => {
            console.error('[StockUpdateNotification] Error listening to stock movements:', error);
        });

        this.subscriptions.set('stockMovements', unsubscribe);
    }

    /**
     * Start listening for cheese stocktake completions
     */
    startListeningCheeseStocktakes() {
        if (this.subscriptions.has('cheeseStocktakes')) {
            return; // Already listening
        }

        // Listen for recently applied cheese stocktakes
        const q = query(
            collection(db, COLLECTIONS.CHEESE_STOCKTAKE_REPORTS),
            where('applied', '==', true)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added' || change.type === 'modified') {
                    const data = change.doc.data();
                    const reportId = change.doc.id;

                    // Skip if we've already processed this
                    if (this.lastProcessedIds.cheeseStocktakes.has(reportId)) {
                        return;
                    }

                    // Check if this was recently applied (within last 5 minutes)
                    const appliedAt = data.appliedAt?.toDate ? data.appliedAt.toDate() : 
                                     (data.appliedAt ? new Date(data.appliedAt) : null);
                    
                    if (appliedAt) {
                        const now = new Date();
                        const timeDiff = now - appliedAt;
                        
                        if (timeDiff < 5 * 60 * 1000) { // Within 5 minutes
                            this.lastProcessedIds.cheeseStocktakes.add(reportId);
                            
                            this.notify('cheese_stocktake_applied', {
                                id: reportId,
                                venueId: data.venueId,
                                venueName: data.venueName || data.venueId,
                                shiftDate: data.shiftDate,
                                staffName: data.staffName || 'Unknown',
                                appliedBy: data.appliedBy || 'unknown',
                                appliedAt: appliedAt,
                                itemsCount: data.items?.length || 0
                            });
                        }
                    }
                }
            });
        }, (error) => {
            console.error('[StockUpdateNotification] Error listening to cheese stocktakes:', error);
        });

        this.subscriptions.set('cheeseStocktakes', unsubscribe);
    }

    /**
     * Start listening for cheese stock adjustments
     */
    startListeningCheeseAdjustments() {
        if (this.subscriptions.has('cheeseAdjustments')) {
            return; // Already listening
        }

        // Listen for recent cheese adjustments
        const q = query(
            collection(db, COLLECTIONS.CHEESE_ADJUSTMENTS),
            where('timestamp', '>', Timestamp.fromDate(new Date(Date.now() - 5 * 60 * 1000))) // Last 5 minutes
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added') {
                    const data = change.doc.data();
                    const adjustmentId = change.doc.id;

                    // Skip if we've already processed this
                    if (this.lastProcessedIds.cheeseAdjustments.has(adjustmentId)) {
                        return;
                    }

                    // Only notify for stocktake-related adjustments
                    if (data.reason === 'stocktake' || data.description?.toLowerCase().includes('stocktake')) {
                        this.lastProcessedIds.cheeseAdjustments.add(adjustmentId);
                        
                        this.notify('cheese_stock_adjusted', {
                            id: adjustmentId,
                            venueId: data.venueId,
                            sku: data.sku,
                            productName: data.productName || data.sku,
                            deltaGrams: data.deltaGrams || 0,
                            previousStock: data.previousStock || 0,
                            newStock: data.newStock || 0,
                            userId: data.userId || 'unknown',
                            reason: data.reason || 'stocktake',
                            description: data.description || '',
                            timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : new Date()
                        });
                    }
                }
            });
        }, (error) => {
            console.error('[StockUpdateNotification] Error listening to cheese adjustments:', error);
        });

        this.subscriptions.set('cheeseAdjustments', unsubscribe);
    }

    /**
     * Start all listeners
     */
    start() {
        this.startListeningStockTakes();
        this.startListeningStockMovements();
        this.startListeningCheeseStocktakes();
        this.startListeningCheeseAdjustments();
    }

    /**
     * Stop all listeners
     */
    stop() {
        this.subscriptions.forEach((unsubscribe) => {
            if (typeof unsubscribe === 'function') {
                unsubscribe();
            }
        });
        this.subscriptions.clear();
    }

    /**
     * Clear processed IDs (useful for testing or reset)
     */
    clearProcessedIds() {
        Object.keys(this.lastProcessedIds).forEach(key => {
            this.lastProcessedIds[key].clear();
        });
    }
}

// Export singleton instance
const stockUpdateNotificationService = new StockUpdateNotificationService();
export default stockUpdateNotificationService;
